/**
 * Article Interface - Unified across all Konstellation sites
 * 
 * This is the single source of truth for article/blog post structure.
 * All sites (RemUp, Info-PER, Info-AV, FlatTax, Notaire) use this interface.
 */

export interface Article {
  /** Unique identifier */
  id: number;
  
  /** URL-friendly identifier (used in /blog/{slug}/) */
  slug: string;
  
  /** Article title (max 60 chars for SEO) */
  title: string;

  /**
   * Titre de la balise <title>, quand `title` (le H1, les listes) est trop long
   * pour les résultats de recherche. Absent : `title` sert aussi de <title>.
   */
  seoTitle?: string;
  
  /** Short description for listing and meta (max 160 chars for SEO) */
  excerpt: string;
  
  /** Author name */
  author: string;
  
  /** Publication date in YYYY-MM-DD format (for scheduling) */
  date: string;
  
  /** Estimated read time (e.g., "12 min") */
  readTime: string;
  
  /** Category for filtering (site-specific values) */
  category: string;
  
  /** Image path relative to public folder (e.g., "/blog-images/article.jpg") */
  image: string;
  
  /** Optional SEO keywords for the article */
  keywords?: string[];

  /** Date de dernière modification (YYYY-MM-DD), utilisée pour le JSON-LD dateModified */
  dateModified?: string;
}

/**
 * Helper: Filter articles to only show published ones (date <= today)
 * Uses YYYY-MM-DD string comparison which is reliable
 */
export function getVisibleArticles(articles: Article[]): Article[] {
  const today = new Date().toISOString().split('T')[0];
  
  return articles
    .filter(article => article.date <= today)
    .sort((a, b) => b.date.localeCompare(a.date)); // Most recent first
}

/**
 * Helper: Get article by slug
 */
export function getArticleBySlug(articles: Article[], slug: string): Article | undefined {
  return articles.find(article => article.slug === slug);
}

/**
 * Helper: Get articles by category
 */
export function getArticlesByCategory(articles: Article[], category: string): Article[] {
  return getVisibleArticles(articles).filter(article => article.category === category);
}

/**
 * Helper: Get unique categories from articles
 */
export function getCategories(articles: Article[]): string[] {
  const visible = getVisibleArticles(articles);
  return Array.from(new Set(visible.map(article => article.category)));
}

/**
 * Helper: Get featured articles (most recent N articles)
 */
export function getFeaturedArticles(articles: Article[], count: number = 3): Article[] {
  return getVisibleArticles(articles).slice(0, count);
}

/**
 * Helper: Get author initials for avatar
 */
export function getAuthorInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
}

